import React from "react";
import { Box, Chip, FormLabel, Tooltip } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import type { Dispatch, SetStateAction } from "react";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import type { FollowUpSheet } from "../../../api/followup/Followup.typed";
import { TrainingStatusCode } from "../../../api/training/Training.typed";
import type { Training } from "../../../api/training/Training.typed";
import { normalizeMultiCommentList } from "../../../api/zekay/comments";
import type { MultiComment } from "../../../api/zekay/comments";
import { Permission, usePermissions } from "../../../contexts/app/permissions";
import { useAuth, useFollowup, useTraining } from "../../../contexts/api";
import useAppSettings from "../../../contexts/app/appSettings";
import { useAlert } from "../../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import { userDisplayName } from "../../../utils/userHelper";
import {
  SQuiz,
  SComment,
  SDate,
  SFile,
  SRedirect,
  SSendMail,
  SchoolOutlinedIcon as SchoolOutlined,
} from "../../../styles/StyledMUI";
import DarkCard from "../../common/DarkCard";
import DarkInput from "../../common/DarkInput";
import DarkTypography from "../../common/DarkTypography";
import CommentsModal from "../../common/CommentsModal";
import DatesComponent, { areAllDatesValid } from "../../common/DatesComponent";
import DocumentsComponent, { getDocumentsBadgeStatus } from "../../common/DocumentsComponent";
import DetailsTooltip from "../../common/DetailsTooltip";
import TheoryTestPanel from "../../shared/theoryTest/TheoryTestPanel";
import {
  hasTheoryTestHistory,
  isTheoryTestValidated,
} from "../../shared/theoryTest/theoryTestHelpers";
import FollowUpSheetAction from "../../shared/followup/FollowUpSheetAction";
import TrainingRowTooltipButton from "../../trainings/common/TrainingRowTooltipButton";
import TrainingStatusActionSelect from "../../trainings/common/TrainingStatusActionSelect";
import type { TrainingStatusAction } from "../../trainings/common/trainingStatusActionTokens";
import { trainingStatusMenuActions } from "../../trainings/common/trainingStatusMenuActions";
import {
  resolveTrainingStatusChip,
  resolveTrainingStatusCode,
} from "../../trainings/common/trainingStatusDisplay";
import { useTrainingMail } from "../../trainings/common/hooks/useTrainingMail";
import {
  trainingCardActionsContainerSx,
  trainingCardActionSlotSx,
} from "../../trainings/common/trainingCardActionsSx";
import ListRowAlertBanner from "../../common/ListRowAlertBanner";
import { trainingAlertLabels } from "../../trainings/common/trainingAlerts";
import {
  colorTokens,
  ctaAccentColor,
  listRowDarkCardSx,
  listRowInactivityAlertSx,
} from "../../../styles/globalStyles";

type TrainingItemProps = {
  training: Training;
  customer: ZekayCustomer | undefined;
  setCustomerUpdated: Dispatch<SetStateAction<ZekayCustomer | undefined>>;
  setTrainingsFormOpen: Dispatch<SetStateAction<boolean>>;
  refreshCustomer: () => Promise<ZekayCustomer | undefined>;
};

export default function TrainingItem(props: TrainingItemProps) {
  const { training, customer, setCustomerUpdated, refreshCustomer } = props;
  const { hasPermission } = usePermissions();
  // Full module parity actions are gated on the Formations write permission.
  const canEditModule = hasPermission(Permission.TRAININGS_EDIT);
  // Redirect to the Formations module only when the user can reach it.
  const canOpenModule = hasPermission(Permission.TRAININGS_ACCESS);

  const navigate = useNavigate();
  const { createFollowUpSheet, getFollowUpSheet } = useFollowup();
  const { sendTrainingInProgressMail } = useTrainingMail();
  const { updateTraining, cancelTraining, validateUserQualification, isTheoryTestOnline } =
    useTraining();
  const { user } = useAuth();
  const { config } = useAppSettings();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const isDesync = config?.CustomersSync === false;

  const [isOpenTheoryTests, setIsOpenTheoryTests] = React.useState(false);
  const [isOpenDocuments, setIsOpenDocuments] = React.useState(false);
  const [isOpenDates, setIsOpenDates] = React.useState(false);
  const [commentsModalOpen, setCommentsModalOpen] = React.useState(false);
  const [testResult] = React.useState(training.TestResult || "");
  const [multiComments, setMultiComments] = React.useState<MultiComment[] | undefined>(() =>
    normalizeMultiCommentList(training.MultiComments as unknown[] | undefined),
  );
  const [theory, setTheory] = React.useState<Dayjs | null>(
    training.Theory && training.Theory.trim() !== "" ? dayjs(training.Theory) : null,
  );
  const [rifap, setRifap] = React.useState<Dayjs | null>(
    training.RifapDate && training.RifapDate.trim() !== "" ? dayjs(training.RifapDate) : null,
  );
  const [sessionsValue, setSessionsValue] = React.useState<string>(String(training.Sessions ?? 0));

  React.useEffect(() => {
    setMultiComments(normalizeMultiCommentList(training.MultiComments as unknown[] | undefined));
  }, [training.MultiComments]);

  React.useEffect(() => {
    setSessionsValue(String(training.Sessions ?? 0));
  }, [training.Sessions]);

  const status = training.Status as string | number | undefined;
  const statusNum = resolveTrainingStatusCode(status);

  const hasQcmDone = hasTheoryTestHistory(testResult, training.TheoryTests);
  const showTheoryTest = isTheoryTestOnline(training.TrainingTemplateID) || hasQcmDone;
  const isQcmValidated = isTheoryTestValidated(testResult, training.TheoryTests);

  const resolved = resolveTrainingStatusChip(status);

  // Maps the Training[] setter expected by shared panels onto customer.Trainings.
  const setTrainingsAdapter = React.useCallback<Dispatch<SetStateAction<Training[] | undefined>>>(
    (update) =>
      setCustomerUpdated((prev) => {
        if (!prev) return prev;
        const current = prev.Trainings ?? [];
        const next =
          typeof update === "function"
            ? (update as (list: Training[] | undefined) => Training[] | undefined)(current)
            : update;

        return { ...prev, Trainings: next ?? [] };
      }),
    [setCustomerUpdated],
  );

  const refreshTrainings = React.useCallback(
    async (): Promise<Training[] | undefined> => (await refreshCustomer())?.Trainings,
    [refreshCustomer],
  );

  const getTrainingsByTemplateIdWrapper = React.useCallback(
    async (_templateId: string): Promise<Training[] | undefined> =>
      (await refreshCustomer())?.Trainings,
    [refreshCustomer],
  );

  const patchTrainingLocally = React.useCallback(
    (patch: Partial<Training>) =>
      setTrainingsAdapter((list) =>
        (list ?? []).map((item) => (item.ID === training.ID ? { ...item, ...patch } : item)),
      ),
    [setTrainingsAdapter, training.ID],
  );

  const hasCustomerEmail = (customer?.MailAddress ?? "").trim() !== "";
  const customerFullName = [customer?.FirstName, customer?.LastName].filter(Boolean).join(" ");
  const sessionsEditable = canEditModule && isDesync;
  // Mail is offered while in progress and once completed (card validation stage).
  const showMail =
    canEditModule &&
    (statusNum === TrainingStatusCode.InProgress || statusNum === TrainingStatusCode.Done);
  const showStatusActions =
    canEditModule &&
    (statusNum === TrainingStatusCode.InProgress ||
      statusNum === TrainingStatusCode.Done ||
      statusNum === TrainingStatusCode.Cancelled);
  const docsStatus = React.useMemo(
    () => getDocumentsBadgeStatus(training, customer),
    [training, customer],
  );

  const statusMenuActionList = React.useMemo((): TrainingStatusAction[] => {
    if (statusNum === TrainingStatusCode.Cancelled) return ["en_cours"];
    // Completed trainings expose the card-validation actions (validate / cancel / reopen).
    if (statusNum === TrainingStatusCode.Done)
      return trainingStatusMenuActions(config?.CustomersSync === true, "cardValidation");

    return trainingStatusMenuActions(config?.CustomersSync === true, "inProgress");
  }, [config?.CustomersSync, statusNum]);

  const handleFollowUpSaved = React.useCallback(
    (updatedFollowUpSheet: FollowUpSheet) => {
      if (!customer?.Trainings) return;
      const nextTrainings = customer.Trainings.map((t) =>
        t.TrainingTemplateID === updatedFollowUpSheet.TrainingTemplateID &&
        t.CustomerID === updatedFollowUpSheet.CustomerId
          ? { ...t, FollowUpSheet: updatedFollowUpSheet }
          : t,
      );
      setCustomerUpdated({ ...customer, Trainings: nextTrainings });
    },
    [customer, setCustomerUpdated],
  );

  const handleCreateFollowUp = React.useCallback(async (): Promise<boolean> => {
    if (!customer?.Id || training.TrainingTemplateID == null) {
      showAlert("Données manquantes pour créer la fiche de suivi", "warning");

      return false;
    }
    const ok = await showConfirmation(
      "Créer la fiche de suivi",
      "Une nouvelle fiche de suivi sera initialisée pour ce client.",
    );
    if (!ok) return false;

    try {
      await createFollowUpSheet({
        CustomerId: customer.Id,
        TrainingTemplateID: training.TrainingTemplateID,
        Logs: [],
      });

      const fresh = await getFollowUpSheet(training.TrainingTemplateID, customer.Id);
      if (!fresh) return false;

      if (customer.Trainings) {
        const nextTrainings = customer.Trainings.map((t) =>
          t.TrainingTemplateID === training.TrainingTemplateID && t.CustomerID === customer.Id
            ? { ...t, FollowUpSheet: fresh }
            : t,
        );
        setCustomerUpdated({ ...customer, Trainings: nextTrainings });
      }

      return true;
    } catch {
      showAlert("Erreur lors de la création de la fiche de suivi", "danger");

      return false;
    }
  }, [
    customer,
    training.TrainingTemplateID,
    createFollowUpSheet,
    getFollowUpSheet,
    setCustomerUpdated,
    showAlert,
    showConfirmation,
  ]);

  const handleSendMail = React.useCallback(
    () => sendTrainingInProgressMail(training),
    [sendTrainingInProgressMail, training],
  );

  // Completed trainings live on the "Cartes" screen; others on the "Formations" list.
  const handleOpenInModule = React.useCallback(() => {
    const isDone = statusNum === TrainingStatusCode.Done;
    const searchQuery = `${customer?.FirstName ?? ""} ${customer?.LastName ?? ""}`.trim();
    navigate("/trainings", {
      state: {
        select: isDone ? "Cartes" : "Formations",
        trainingTemplateId: training.TrainingTemplateID,
        trainingStatus: statusNum,
        targetTrainingId: training.ID,
        targetCustomerId: training.CustomerID,
        searchQuery,
      },
    });
  }, [
    navigate,
    statusNum,
    training.TrainingTemplateID,
    training.ID,
    training.CustomerID,
    customer?.FirstName,
    customer?.LastName,
  ]);

  const handleCommentsSave = React.useCallback(
    async (nextMulti?: MultiComment[]) => {
      const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
      const updatedTraining = {
        ...trainingPayload,
        MultiComments: nextMulti ?? multiComments,
        UpdatedBy: userDisplayName(user),
      };

      try {
        await updateTraining(updatedTraining);
        const normalized = normalizeMultiCommentList(
          updatedTraining.MultiComments as unknown[] | undefined,
        );
        setMultiComments(normalized);
        patchTrainingLocally({ MultiComments: normalized });
      } catch {
        showAlert("Erreur lors de la mise à jour des commentaires", "danger");
      }
    },
    [training, multiComments, user, updateTraining, patchTrainingLocally, showAlert],
  );

  const commitSessions = React.useCallback(async () => {
    const parsed = parseInt(sessionsValue.trim(), 10);
    const next = Number.isNaN(parsed) || parsed < 0 ? 0 : parsed;

    if (next !== parsed) {
      setSessionsValue(String(next));
    }

    if (next === (training.Sessions ?? 0)) return;

    const updatedTraining: Training = {
      ...training,
      Sessions: next,
      UpdatedBy: userDisplayName(user),
      UpdatedAt: new Date(),
    };

    try {
      await updateTraining(updatedTraining);
      patchTrainingLocally({ Sessions: next });
    } catch {
      setSessionsValue(String(training.Sessions ?? 0));
      showAlert("Erreur lors de la mise à jour du nombre de séances", "danger");
    }
  }, [sessionsValue, training, user, updateTraining, patchTrainingLocally, showAlert]);

  const handleStatusAction = React.useCallback(
    async (action: TrainingStatusAction) => {
      if (action === "terminer" && statusNum !== TrainingStatusCode.InProgress) return;

      if (action === "valider") {
        const ok = await showConfirmation(
          "Valider la qualification",
          "Êtes-vous sûr de vouloir valider cette qualification ?",
        );
        if (!ok) return;
        try {
          await validateUserQualification(
            { ...training, Status: "Validé", UpdatedBy: userDisplayName(user) },
            { skipConfirmation: true },
          );
          await refreshCustomer();
        } catch {
          showAlert("Erreur lors de la validation de la qualification", "danger");
        }

        return;
      }

      if (action === "annuler") {
        const ok = await showConfirmation(
          "Annuler la formation",
          "Confirmer l'annulation de cette formation ?",
        );
        if (!ok) return;
        try {
          await cancelTraining(training, { skipConfirmation: true });
          await refreshCustomer();
        } catch {
          showAlert("Erreur lors de l'annulation de la formation", "danger");
        }

        return;
      }

      const nextStatus = action === "en_cours" ? "En cours" : "Terminé";
      const title = action === "en_cours" ? "Remettre en cours" : "Terminer la formation";
      const message =
        action === "en_cours"
          ? "Repasser la formation en « En cours » ?"
          : "Passer cette formation en « Terminé » ?";

      const ok = await showConfirmation(title, message);
      if (!ok) return;

      try {
        await updateTraining({
          ...training,
          Status: nextStatus,
          UpdatedBy: userDisplayName(user),
          UpdatedAt: new Date(),
        });
        await refreshCustomer();
      } catch {
        showAlert("Erreur lors de la mise à jour du statut", "danger");
      }
    },
    [
      training,
      user,
      statusNum,
      cancelTraining,
      updateTraining,
      validateUserQualification,
      refreshCustomer,
      showConfirmation,
      showAlert,
    ],
  );

  const alertLabels = trainingAlertLabels(training);
  const hasAlert = alertLabels.length > 0;
  const qualificationTitle = (training.Qualification ?? "").trim() || "—";
  const agencyLine = (training.Agency ?? "").trim();
  const needsDates = Boolean(training.NeedsTheory || training.NeedsRifap);

  return (
    <>
      <DarkCard
        dense
        sx={{
          ...listRowDarkCardSx,
          ...(hasAlert ? listRowInactivityAlertSx : {}),
        }}
      >
        <ListRowAlertBanner labels={alertLabels} />
        <Box sx={trainingCardActionsContainerSx}>
          <Box sx={trainingCardActionSlotSx} aria-hidden>
            <Tooltip title="Formation">
              <SchoolOutlined sx={{ fontSize: { xs: 19, sm: 20 }, color: ctaAccentColor }} />
            </Tooltip>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1, sm: 1.5 },
              minWidth: 0,
              flexWrap: { sm: "wrap" },
              flex: 1,
            }}
          >
            <DarkTypography
              level="h4"
              colorVariant="primary"
              sx={{
                fontWeight: 600,
                minWidth: 0,
                fontSize: { xs: "0.9375rem", sm: "1.0625rem", md: "1.125rem" },
                lineHeight: 1.35,
              }}
            >
              {qualificationTitle}
            </DarkTypography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: { xs: 1, sm: 1.25 },
                minWidth: 0,
              }}
            >
              {agencyLine && (
                <DarkTypography
                  level="body-md"
                  colorVariant="primary"
                  sx={{
                    fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                    lineHeight: 1.4,
                  }}
                >
                  {agencyLine}
                </DarkTypography>
              )}
              <Chip size="sm" color={resolved.color} variant="soft" sx={{ maxWidth: "100%" }}>
                {resolved.label}
              </Chip>
              {sessionsEditable && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <FormLabel
                    htmlFor={`customer-training-sessions-${training.ID}`}
                    sx={{ fontSize: 10, lineHeight: 1, m: 0, color: colorTokens.text.dimmed }}
                  >
                    Séances
                  </FormLabel>
                  <DarkInput
                    id={`customer-training-sessions-${training.ID}`}
                    type="number"
                    size="sm"
                    aria-label="Nombre de séances"
                    value={sessionsValue}
                    onChange={(e) => setSessionsValue(e.target.value)}
                    onBlur={() => void commitSessions()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        (e.target as HTMLInputElement).blur();
                      }
                    }}
                    slotProps={{
                      input: {
                        type: "number",
                        min: 0,
                        max: 999,
                        step: 1,
                        inputMode: "numeric",
                      },
                    }}
                    sx={{ width: 64, minHeight: 28 }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", flexWrap: "wrap" }}>
            {canEditModule && (
              <TrainingRowTooltipButton
                title="Commentaires"
                ariaLabel="Commentaires"
                icon={<SComment />}
                onClick={() => setCommentsModalOpen(true)}
                ctaVariant="accent"
                badgeContent={multiComments?.length ?? 0}
                badgeColor="neutral"
              />
            )}

            {canEditModule && needsDates && (
              <TrainingRowTooltipButton
                title="Sessions théoriques"
                ariaLabel="Sessions théoriques"
                icon={<SDate />}
                onClick={() => setIsOpenDates((prev) => !prev)}
                ctaVariant="accent"
                badgeContent={areAllDatesValid(training, theory, rifap) ? "✓" : 0}
                badgeColor="success"
              />
            )}

            {canEditModule && (
              <TrainingRowTooltipButton
                title="Docs"
                ariaLabel="Documents"
                icon={<SFile />}
                onClick={() => setIsOpenDocuments((prev) => !prev)}
                ctaVariant="accent"
                badgeContent={
                  docsStatus.allUploaded ? "✓" : `${docsStatus.uploaded}/${docsStatus.total}`
                }
                badgeColor={docsStatus.allUploaded ? "success" : "neutral"}
              />
            )}

            {showTheoryTest && (
              <TrainingRowTooltipButton
                title={hasQcmDone ? "Voir les QCM" : "Aucun QCM passé"}
                ariaLabel="Tests théoriques"
                icon={<SQuiz />}
                onClick={hasQcmDone ? () => setIsOpenTheoryTests(!isOpenTheoryTests) : undefined}
                disabled={!hasQcmDone}
                ctaVariant="accent"
                badgeContent={isQcmValidated ? "✓" : 0}
                badgeColor="success"
              />
            )}

            <FollowUpSheetAction
              training={training}
              customerName={customerFullName}
              onSaved={handleFollowUpSaved}
              onCreate={handleCreateFollowUp}
            />

            {showMail && (
              <TrainingRowTooltipButton
                title={hasCustomerEmail ? "Mail" : "Email client manquant"}
                ariaLabel={hasCustomerEmail ? "Envoyer mail" : "Mail indisponible"}
                icon={<SSendMail />}
                onClick={hasCustomerEmail ? handleSendMail : undefined}
                disabled={!hasCustomerEmail}
                ctaVariant="accent"
              />
            )}

            {showStatusActions && (
              <TrainingStatusActionSelect
                listKey={training.ID ?? training.CustomerID ?? 0}
                allowedActions={statusMenuActionList}
                onAction={handleStatusAction}
              />
            )}

            {canEditModule && <DetailsTooltip training={training} />}

            {canOpenModule && (
              <TrainingRowTooltipButton
                title="Ouvrir dans le module Formations"
                ariaLabel="Ouvrir dans le module Formations"
                icon={<SRedirect />}
                onClick={handleOpenInModule}
                ctaVariant="accent"
              />
            )}
          </Box>
        </Box>

        {canEditModule && isOpenDocuments && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <DocumentsComponent
              training={training}
              customer={customer}
              setTrainings={setTrainingsAdapter}
              refreshTrainings={refreshTrainings}
              onCustomerUpdated={(next) => setCustomerUpdated(next)}
              standalone
            />
          </Box>
        )}

        {canEditModule && isOpenDates && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <DatesComponent
              training={training}
              theory={theory}
              rifap={rifap}
              setTheory={setTheory}
              setRifap={setRifap}
              updateTraining={updateTraining}
              getTrainingsByTemplateId={getTrainingsByTemplateIdWrapper}
              setTrainings={setTrainingsAdapter}
              isOpenDates={isOpenDates}
              status={statusNum}
              standalone
            />
          </Box>
        )}

        {isOpenTheoryTests && showTheoryTest && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <TheoryTestPanel
              training={training}
              testResult={testResult}
              isOpenTheoryTests={isOpenTheoryTests}
              status={statusNum}
              standalone
            />
          </Box>
        )}
      </DarkCard>

      {canEditModule && (
        <CommentsModal
          open={commentsModalOpen}
          title="Commentaires"
          currentUserName={userDisplayName(user)}
          multiComments={multiComments}
          setMultiComments={setMultiComments}
          onSave={handleCommentsSave}
          onClose={() => setCommentsModalOpen(false)}
          allowEdit={canEditModule}
        />
      )}
    </>
  );
}
